import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { useState } from "react";

import { useSectionDrawingPreviewQuery } from "../../hooks/queries/useSectionDrawingPreviewQuery";
import type { SectionSpec } from "@/shared/models/SectionSpec";
import { formatSectionSpecRevision } from "@/shared/utils/sectionSpecRevision";
import { SECTION_SPEC_FILE_KIND } from "@/shared/models/SectionSpec";
import {
	formatSpecValue as num,
	formatWeightBand,
	SectionDetailStat,
	SectionDrawingCard,
	SectionDrawingDialog,
	sectionSpecEmptyState,
} from "../../shared/components/SectionDetailsPanelContent";

interface Props {
	sectionId: number | null;
	spec: SectionSpec | null;
	pending?: boolean;
	hasConfirmedRevisions: boolean;
}

export const SectionDetailsPanel = ({
	sectionId,
	spec,
	pending = false,
	hasConfirmedRevisions,
}: Props) => {
	const [openedKind, setOpenedKind] = useState<
		| typeof SECTION_SPEC_FILE_KIND.MILL_DRAWING
		| typeof SECTION_SPEC_FILE_KIND.DIMENSIONS_REFERENCE
		| null
	>(null);
	const millPreview = useSectionDrawingPreviewQuery({
		fileId: spec?.MillDrawingUploadFileId ?? null,
		fileName: spec?.MillDrawingFileName ?? null,
	});
	const referencePreview = useSectionDrawingPreviewQuery({
		fileId: spec?.DimensionsReferenceUploadFileId ?? null,
		fileName: spec?.DimensionsReferenceFileName ?? null,
	});

	const emptyState = sectionSpecEmptyState({
		sectionId,
		spec,
		pending,
		hasConfirmedRevisions,
	});
	if (emptyState || !spec) return emptyState;

	const weightBand = formatWeightBand(spec);
	const millTitle = spec.PrintNo ? `Drawing ${spec.PrintNo}` : "Mill drawing";
	const isMillDrawing = openedKind === SECTION_SPEC_FILE_KIND.MILL_DRAWING;

	return (
		<Stack spacing={1.5} sx={{ height: "100%", minHeight: 0 }}>
			<Stack
				direction="row"
				spacing={1}
				alignItems="center"
				flexWrap="wrap"
				useFlexGap
				flexShrink={0}
			>
				<Typography variant="subtitle2" fontWeight={700}>
					Section details
				</Typography>
				<Chip
					size="small"
					color="primary"
					label={formatSectionSpecRevision(spec.Rev, spec.SubRev)}
				/>
				{spec.SectionDescription && (
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ minWidth: 0, overflowWrap: "anywhere" }}
					>
						{spec.SectionDescription}
					</Typography>
				)}
			</Stack>

			<Box
				sx={{
					display: "grid",
					flex: 1,
					gridTemplateColumns: {
						xs: "minmax(0, 1fr)",
						lg: "minmax(200px, 0.85fr) minmax(0, 2.15fr)",
					},
					gridTemplateRows: { lg: "minmax(0, 1fr)" },
					gap: 1.5,
					minHeight: { xs: "auto", lg: 0 },
					alignItems: "stretch",
				}}
			>
				<Stack spacing={1.5} sx={{ minWidth: 0, minHeight: 0 }}>
					<Stack spacing={1} flexShrink={0}>
						<SectionDetailStat label="Print no." value={spec.PrintNo ?? "-"} />
						<SectionDetailStat label="Area sq.in." value={num(spec.Area)} />
						<SectionDetailStat
							label="Leg delta max"
							value={num(spec.LegDeltaMax)}
						/>
						<SectionDetailStat
							label="Weight lb/ft"
							value={num(spec.TargetLbsPerFoot)}
							hint={spec.TargetLbsPerFoot === null ? null : weightBand}
						/>
					</Stack>

					<Box
						sx={{
							display: "grid",
							flex: 1,
							gridTemplateRows: "minmax(0, 1fr)",
							minHeight: { xs: 180, lg: 0 },
						}}
					>
						<SectionDrawingCard
							key={
								spec.DimensionsReferenceUploadFileId ??
								"missing-reference-image"
							}
							spec={spec}
							kind={SECTION_SPEC_FILE_KIND.DIMENSIONS_REFERENCE}
							fileName={spec.DimensionsReferenceFileName}
							title="Dimensions reference"
							icon={<StraightenOutlinedIcon fontSize="small" />}
							missingMessage="Reference image not uploaded yet"
							preview={referencePreview}
							onOpen={() =>
								setOpenedKind(SECTION_SPEC_FILE_KIND.DIMENSIONS_REFERENCE)
							}
						/>
					</Box>
				</Stack>

				<SectionDrawingCard
					key={spec.MillDrawingUploadFileId ?? "missing-mill-drawing"}
					spec={spec}
					kind={SECTION_SPEC_FILE_KIND.MILL_DRAWING}
					fileName={spec.MillDrawingFileName}
					title="Mill drawing"
					icon={<DescriptionOutlinedIcon fontSize="small" />}
					missingMessage="Mill drawing not uploaded yet"
					preview={millPreview}
					onOpen={() => setOpenedKind(SECTION_SPEC_FILE_KIND.MILL_DRAWING)}
				/>
			</Box>

			{openedKind !== null && (
				<SectionDrawingDialog
					title={isMillDrawing ? millTitle : "Dimensions reference"}
					label={isMillDrawing ? "Mill drawing" : "Dimensions reference"}
					preview={isMillDrawing ? millPreview : referencePreview}
					onClose={() => setOpenedKind(null)}
				/>
			)}
		</Stack>
	);
};
